// Brief 63 (23 Jun 2026): "AI Sweep" branding retired. The only AI
// action surfaced in the editor is the "AI Once-Over". Comments rail
// labels suggestions "AI Once-Over" and its empty-state copy points
// to that button.
// Brief 69.1 (24 Jun 2026): the rail now owns the AI Once-Over trigger —
// an empty-state button plus a small re-run button in the header — so the
// action is discoverable next to the suggestions it produces.
import React, { useMemo, useRef, useEffect, useState } from "react";
import {
  Sparkles,
  Check,
  X,
  CornerUpLeft,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  Info,
  HelpCircle,
  Send,
  Loader2,
} from "lucide-react";
import { FIRM_NAME } from "../config/constants";

/**
 * CclCommentsRail — Word/Docs-style anchored AI comments rail.
 *
 * Replaces the old floating overlay. Each AI suggestion is a comment card
 * anchored to a paragraph/range in the document. Clicking a card scrolls
 * the document to its anchor; the active anchor is highlighted via the
 * `activeAnchorId` prop driven by the editor side.
 *
 * Comment model (one normalised shape for sweep + gap suggestions):
 *   {
 *     id: string,
 *     anchorId: string | null,   // data-anchor-id in the document
 *     kind: "suggestion" | "warning" | "missing-info",
 *     title: string,             // short scannable headline
 *     body: string,              // suggestion detail (not a wall of text)
 *     quote: string | null,      // optional snippet of the doc being acted on
 *     suggestedText: string | null, // text applied on Accept (null = advisory)
 *     timestamp: string | number | Date | null,
 *     status: "open" | "accepted" | "rejected",
 *   }
 *
 * Props:
 *   comments        — Comment[]
 *   collapsed       — boolean (rail closed -> document expands)
 *   onToggleCollapse() — flip collapsed
 *   onAccept(id)    — apply suggested edit, mark resolved
 *   onReject(id)    — mark resolved, no doc change
 *   onReply(id, text) — send feedback text; re-drafts suggestedText using it (BM-H121)
 *   onDraft(comment) — generate replacement copy for a gap (fills suggestedText)
 *   onEditDraft(id, text) — hand-edit the drafted suggestion before Insert (BM-H121)
 *   onDiscard(id)   — drop a generated draft, returning the card to advisory state
 *   onFocusAnchor(anchorId) — scroll the document to the anchor
 *   onRunSweep()    — run the AI Once-Over (empty-state + header trigger)
 *   sweeping        — boolean, true while a sweep is in flight
 *   activeAnchorId  — anchor currently focused in the document (drives highlight)
 *   authorLabel     — defaults to "AI Once-Over"
 */

const KIND_META = {
  suggestion: {
    label: "Suggestion",
    Icon: Sparkles,
    cls: "text-teal bg-teal-dim border-teal/30",
  },
  warning: {
    label: "Warning",
    Icon: AlertTriangle,
    cls: "text-status-amber bg-status-amber/10 border-amber-500/30",
  },
  "missing-info": {
    label: "Missing info",
    Icon: HelpCircle,
    cls: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  },
};

// BM-H3747: what an advisory card tells the fee earner. A point the model
// itself marked advisory is something to check, not an edit; anything else
// is a change the AI suggested but could not place exactly in the letter.
export function removalPreview(comment) {
  const n = comment?.removeCount || 1;
  const cut = (t) => (t && t.length > 70 ? `${t.slice(0, 70)}...` : t || "");
  const first = cut(comment?.blockText);
  const last = cut(comment?.endBlockText);
  const what = n === 1 ? "1 paragraph" : `${n} paragraphs`;
  if (n === 1 || !last || last === first) return `Removes ${what}: "${first}"`;
  return `Removes ${what}, from "${first}" to "${last}"`;
}

function formatDay(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// BM-H3747 (19 Oct 2026): what a reply did, in one sentence.
export function replyOutcome(comment) {
  const reply = comment?.reply || {};
  if (reply.op === "none") {
    return reply.kept
      ? "Noted: no change to the letter. The next once-over will not raise this point again."
      : "Noted: no change to the letter. Your reply could not be saved, so a rerun may raise this point again.";
  }
  const placed = (reply.op || comment?.op) !== "advisory";
  const now = placed
    ? "Redrafted from your reply. It is not in the letter yet: press Insert into letter to put it in."
    : "Redrafted from your reply, shown below. The AI could not find where it goes in the letter, so it cannot be inserted in one click.";
  const later = reply.kept
    ? " Your reply is saved, and the next once-over will use your wording."
    : " Your reply could not be saved, so a rerun will not know about it.";
  return now + later;
}

export function advisoryNote(comment) {
  const reason = comment?.advisoryReason || "";
  if (!reason || reason === "model_advisory") {
    return "A point to check. Nothing needs adding to the letter unless you decide it does.";
  }
  return "The AI could not find the exact place for this change. Rerun the once-over, or edit the letter yourself.";
}

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function CommentCard({
  comment,
  active,
  authorLabel,
  cardRef,
  onAccept,
  onReject,
  onReply,
  onDraft,
  onEditDraft,
  onDiscard,
  onFocusAnchor,
  onUndo,
}) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const resolved = comment.status !== "open";
  // BM-H596: no exact place in the letter, so never an Insert button.
  const advisory = comment.advisory === true || comment.op === "advisory";
  const meta = KIND_META[comment.kind] || KIND_META.suggestion;
  const { Icon } = meta;

  const handleCardActivate = () => {
    if (comment.anchorId) onFocusAnchor?.(comment.anchorId);
  };

  const handleReplySend = () => {
    const text = replyText.trim();
    if (!text) return;
    onReply?.(comment.id, text);
    setReplyText("");
    setReplyOpen(false);
  };

  return (
    <li
      ref={cardRef}
      data-testid={`ccl-comment-${comment.id}`}
      data-comment-status={comment.status}
      className={`group relative rounded-[10px] border bg-navy-card transition-all duration-150 ${
        active
          ? "border-teal/60 shadow-lg shadow-teal-glow"
          : "border-navy-border hover:border-teal/30 hover:shadow-md hover:shadow-black/20"
      } ${resolved ? "opacity-50" : ""}`}
    >
      {/* Connecting spine — subtle teal margin marking the anchored card */}
      <span
        aria-hidden="true"
        className={`absolute left-0 top-3 bottom-3 w-0.5 rounded-full transition-colors duration-150 ${
          active ? "bg-teal" : "bg-teal/30 group-hover:bg-teal/50"
        }`}
      />

      <button
        type="button"
        onClick={handleCardActivate}
        className="w-full text-left px-4 pt-3.5 pb-2 rounded-t-[10px] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70 focus-visible:ring-inset"
        aria-label={`${comment.title}. ${comment.anchorId ? "Activate to scroll document to the related paragraph." : ""}`}
      >
        {/* Author row */}
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-dim text-teal"
          >
            <Sparkles size={13} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-txt-muted">
              {authorLabel}
            </span>
            {comment.timestamp && (
              <span className="text-[10px] text-txt-faint">
                {formatTimestamp(comment.timestamp)}
              </span>
            )}
          </div>
          <span
            className={`flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${meta.cls}`}
          >
            <Icon size={10} />
            {meta.label}
          </span>
        </div>

        {/* Title + body */}
        <p
          className={`mt-2.5 text-[13px] font-semibold leading-snug text-txt ${
            resolved ? "line-through" : ""
          }`}
        >
          {comment.title}
        </p>
        {comment.body && (
          <p className="mt-1.5 text-[12.5px] leading-[1.5] text-txt-muted">
            {comment.body}
          </p>
        )}

        {/* Quoted document snippet */}
        {comment.quote && (
          <blockquote className="mt-2.5 border-l-2 border-navy-border pl-2.5 text-[11.5px] italic leading-[1.5] text-txt-faint">
            {comment.quote}
          </blockquote>
        )}
      </button>

      {/* Actions / resolved state */}
      <div className="px-4 pb-3.5 pt-1">
        {resolved ? (
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-txt-faint"
              data-testid={`ccl-comment-resolved-${comment.id}`}
            >
              {comment.status === "accepted" ? (
                <>
                  <Check size={12} className="text-status-green" /> Accepted
                </>
              ) : (
                <>
                  <X size={12} /> Rejected
                </>
              )}
            </span>
            {/* BM-H17 — Undo lets the fee earner reopen a decision. */}
            <button
              type="button"
              onClick={() => onUndo?.(comment.id)}
              data-testid={`ccl-comment-undo-${comment.id}`}
              className="inline-flex items-center gap-1 rounded-md border border-navy-border bg-transparent px-2 py-0.5 text-[10.5px] font-medium text-txt-muted transition-colors duration-150 hover:bg-navy-hover hover:text-txt focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70"
              title="Reopen this suggestion"
            >
              <CornerUpLeft size={11} /> Undo
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {/* BM-H596 (23 Sep 2026): an advisory card has no exact place in
                the letter, so it shows the reason and quote only: no Insert,
                no Draft fix that could produce an unplaced insert. A stale
                card (its part of the letter changed since the check) keeps
                its text but Apply is disabled. */}
            {/* BM-H3747 (5 Oct 2026): "Make any change by hand" read as the
                AI giving up. Say which it is: a point to check that is not a
                letter edit, or a change the AI could not place exactly. */}
            {/* BM-H3747 (Vanessa, 19 Oct 2026): "it is not clear if these are
                then incorporated or responded to like a conversation". Say
                what a reply did: it redrafts this card, nothing is in the
                letter until Insert, and whether it is kept for the rerun. */}
            {comment.reply && (
              <div className="rounded-lg border border-teal/25 bg-teal-dim/40 px-2.5 py-2 text-[11px] leading-[1.5]" data-testid={`ccl-comment-reply-outcome-${comment.id}`}>
                <p className="text-txt-muted">
                  You replied: <span className="italic text-txt">&ldquo;{comment.reply.feedback}&rdquo;</span>
                </p>
                {/* BM-H3747 (19 Oct 2026, 17:05): the AI's answer to her reply. */}
                {comment.reply.note && (
                  <p className="mt-1 text-txt" data-testid={`ccl-comment-reply-answer-${comment.id}`}>
                    AI: {comment.reply.note}
                  </p>
                )}
                <p className="mt-1 text-txt" data-testid={`ccl-comment-reply-status-${comment.id}`}>
                  {replyOutcome(comment)}
                </p>
              </div>
            )}
            {!comment.reply && comment.fromReply && (
              <p className="text-[11px] text-teal" data-testid={`ccl-comment-from-reply-${comment.id}`}>
                Raised again with the wording from your reply{comment.fromReply.at ? ` of ${formatDay(comment.fromReply.at)}` : ""}: &ldquo;{comment.fromReply.feedback}&rdquo;
              </p>
            )}
            {advisory && comment.replyWording && (
              <p className="whitespace-pre-wrap rounded-lg border border-navy-border bg-navy px-2.5 py-2 text-[11.5px] leading-[1.5] text-txt" data-testid={`ccl-comment-reply-wording-${comment.id}`}>
                {comment.replyWording}
              </p>
            )}
            {advisory && !comment.reply && (
              <p className="text-[11px] text-txt-faint" data-testid={`ccl-comment-advisory-${comment.id}`}>
                {advisoryNote(comment)}
              </p>
            )}
            {/* BM-H3747: what a "remove" fix takes out, so the fee earner
                can see it before clicking. */}
            {!advisory && comment.op === "remove" && (
              <p className="text-[11px] text-txt-muted" data-testid={`ccl-comment-remove-preview-${comment.id}`}>
                {removalPreview(comment)}
              </p>
            )}
            {comment.stale && (
              <p className="text-[11px] font-medium text-status-amber" data-testid={`ccl-comment-stale-${comment.id}`}>
                This part of the letter has changed since the check. Rerun the once-over
              </p>
            )}
            {/* Drafted gap fix — editable preview of the generated copy
                before inserting (BM-H121). Fee earners can hand-tune the
                AI's wording without a round trip through Reply. */}
            {!advisory && comment.suggestedText != null && comment.gapLabel && (
              <textarea
                value={comment.suggestedText}
                onChange={(e) => onEditDraft?.(comment.id, e.target.value)}
                rows={Math.min(10, Math.max(3, comment.suggestedText.split("\n").length))}
                data-testid={`ccl-comment-draft-${comment.id}`}
                className="w-full resize-y rounded-lg border border-navy-border bg-navy px-2.5 py-2 text-[11.5px] leading-[1.5] text-txt focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70"
                placeholder="Drafted text will appear here"
              />
            )}
            <div className="flex flex-wrap items-center gap-2">
              {advisory ? null : comment.op === "remove" ? (
                <button
                  type="button"
                  onClick={() => onAccept?.(comment.id)}
                  disabled={Boolean(comment.stale)}
                  data-testid={`ccl-comment-remove-${comment.id}`}
                  className="disabled:cursor-not-allowed disabled:opacity-50 inline-flex items-center gap-1.5 rounded-lg border border-green-500/25 bg-status-green/15 px-2.5 py-1 text-[11.5px] font-medium text-status-green transition-colors duration-150 hover:bg-status-green/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-status-green/70"
                >
                  <Check size={12} /> Remove from letter
                </button>
              ) : comment.suggestedText ? (
                <button
                  type="button"
                  onClick={() => onAccept?.(comment.id)}
                  disabled={Boolean(comment.stale)}
                  data-testid={`ccl-comment-accept-${comment.id}`}
                  className="disabled:cursor-not-allowed disabled:opacity-50 inline-flex items-center gap-1.5 rounded-lg border border-green-500/25 bg-status-green/15 px-2.5 py-1 text-[11.5px] font-medium text-status-green transition-colors duration-150 hover:bg-status-green/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-status-green/70"
                >
                  <Check size={12} /> {comment.gapLabel ? "Insert into letter" : "Accept"}
                </button>
              ) : (
                (comment.gapLabel || comment.drafting) && (
                  <button
                    type="button"
                    onClick={() => onDraft?.(comment)}
                    disabled={comment.drafting}
                    data-testid={`ccl-comment-draft-btn-${comment.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-teal/30 bg-teal-dim px-2.5 py-1 text-[11.5px] font-medium text-teal transition-colors duration-150 hover:bg-teal/20 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70"
                  >
                    {comment.drafting ? (
                      <><Loader2 size={12} className="animate-spin" /> Drafting…</>
                    ) : (
                      <><Sparkles size={12} /> Draft fix</>
                    )}
                  </button>
                )
              )}
              {!advisory && comment.suggestedText && comment.gapLabel && (
                <button
                  type="button"
                  onClick={() => onDiscard?.(comment.id)}
                  data-testid={`ccl-comment-discard-${comment.id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-navy-border bg-transparent px-2.5 py-1 text-[11.5px] font-medium text-txt-muted transition-colors duration-150 hover:bg-navy-hover hover:text-txt focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70"
                >
                  <X size={12} /> Discard
                </button>
              )}
              <button
                type="button"
                onClick={() => onReject?.(comment.id)}
                data-testid={`ccl-comment-reject-${comment.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-navy-border bg-transparent px-2.5 py-1 text-[11.5px] font-medium text-txt-muted transition-colors duration-150 hover:bg-navy-hover hover:text-txt focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70"
              >
                <X size={12} /> Reject
              </button>
              <button
                type="button"
                onClick={() => setReplyOpen((v) => !v)}
                aria-expanded={replyOpen}
                data-testid={`ccl-comment-reply-${comment.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-navy-border bg-transparent px-2.5 py-1 text-[11.5px] font-medium text-txt-muted transition-colors duration-150 hover:bg-navy-hover hover:text-txt focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70"
              >
                <CornerUpLeft size={12} /> Reply
              </button>
            </div>
          </div>
        )}

        {/* Reply / Refine — Phase 2 UI stub, scoped to this anchor */}
        {replyOpen && !resolved && (
          <div className="mt-2.5" data-testid={`ccl-comment-reply-box-${comment.id}`}>
            <label className="sr-only" htmlFor={`reply-${comment.id}`}>
              Refine this suggestion
            </label>
            <textarea
              id={`reply-${comment.id}`}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={2}
              placeholder="Tell the AI what to change. It redrafts this suggestion for you to insert"
              className="w-full resize-y rounded-lg border border-navy-border bg-navy px-2.5 py-2 text-[12px] leading-[1.5] text-txt placeholder:text-txt-faint focus:border-teal/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/40"
            />
            <div className="mt-1.5 flex justify-end">
              <button
                type="button"
                onClick={handleReplySend}
                disabled={!replyText.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal px-2.5 py-1 text-[11.5px] font-medium text-white transition-colors duration-150 hover:bg-teal-hover disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70"
              >
                <Send size={11} /> Send
              </button>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

export default function CclCommentsRail({
  comments = [],
  collapsed = false,
  onToggleCollapse,
  onAccept,
  onReject,
  onUndo,
  onReply,
  onDraft,
  onEditDraft,
  onDiscard,
  onFocusAnchor,
  onRunSweep,
  sweeping = false,
  activeAnchorId = null,
  authorLabel,
  notice = "",
}) {
  const [showResolved, setShowResolved] = useState(false);
  const cardRefs = useRef({});
  const label = authorLabel || `AI Once-Over`;

  const { openCount, resolvedCount } = useMemo(() => {
    let open = 0;
    let resolved = 0;
    comments.forEach((c) => (c.status === "open" ? open++ : resolved++));
    return { openCount: open, resolvedCount: resolved };
  }, [comments]);

  const visible = useMemo(
    () => (showResolved ? comments : comments.filter((c) => c.status === "open")),
    [comments, showResolved],
  );

  // When the document focuses an anchor, scroll the matching card into view.
  useEffect(() => {
    if (!activeAnchorId) return;
    const match = comments.find((c) => c.anchorId === activeAnchorId);
    if (!match) return;
    const node = cardRefs.current[match.id];
    if (node) node.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeAnchorId, comments]);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onToggleCollapse}
        data-testid="ccl-rail-expand"
        aria-label="Open AI comments rail"
        aria-expanded="false"
        className="group flex shrink-0 flex-col items-center gap-2 rounded-l-xl border border-r-0 border-navy-border bg-navy-card px-2 py-3 text-txt-muted transition-colors duration-150 hover:text-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70"
      >
        <ChevronLeft size={16} />
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-dim text-teal">
          <Sparkles size={12} />
        </span>
        {openCount > 0 && (
          <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-teal px-1 text-[10px] font-semibold text-white">
            {openCount}
          </span>
        )}
        <span
          className="text-[10px] font-medium uppercase tracking-wider"
          style={{ writingMode: "vertical-rl" }}
        >
          Comments
        </span>
      </button>
    );
  }

  return (
    <aside
      data-testid="ccl-comments-rail"
      aria-label="AI suggestions"
      className="flex w-full shrink-0 flex-col rounded-xl border border-navy-border bg-navy lg:w-[372px]"
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-navy-border px-4 py-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-dim text-teal">
          <Sparkles size={14} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <h2 className="text-[13px] font-semibold text-txt">{label}</h2>
          <p className="text-[11px] text-txt-muted" data-testid="ccl-rail-count">
            {openCount} {openCount === 1 ? "suggestion" : "suggestions"}
            {resolvedCount > 0 ? `, ${resolvedCount} resolved` : ""}
          </p>
        </div>
        {onRunSweep && comments.length > 0 && (
          <button
            type="button"
            onClick={onRunSweep}
            disabled={sweeping}
            data-testid="ccl-rail-rerun-sweep"
            title="Re-run AI Once-Over"
            className="flex h-8 items-center gap-1 rounded-lg border border-navy-border px-2 text-[11px] font-medium text-txt-muted transition-colors duration-150 hover:bg-navy-hover hover:text-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sweeping ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Sparkles size={12} />
            )}
            Re-run
          </button>
        )}
        <button
          type="button"
          onClick={onToggleCollapse}
          data-testid="ccl-rail-collapse"
          aria-label="Close AI comments rail"
          aria-expanded="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-txt-muted transition-colors duration-150 hover:bg-navy-hover hover:text-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      {notice && (
        <p className="border-b border-navy-border px-4 py-2 text-[11px] text-teal" data-testid="ccl-rail-reply-notice">
          {notice}
        </p>
      )}

      {/* Resolved toggle */}
      {resolvedCount > 0 && (
        <div className="flex items-center justify-end border-b border-navy-border px-4 py-1.5">
          <button
            type="button"
            onClick={() => setShowResolved((v) => !v)}
            aria-pressed={showResolved}
            data-testid="ccl-rail-show-resolved"
            className="text-[11px] font-medium text-txt-muted transition-colors duration-150 hover:text-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70 rounded"
          >
            {showResolved ? "Hide resolved" : `Show resolved (${resolvedCount})`}
          </button>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-3 py-3" style={{ maxHeight: "560px" }}>
        {visible.length === 0 ? (
          <div
            className="flex h-full min-h-[120px] flex-col items-center justify-center px-6 text-center"
            data-testid="ccl-rail-empty"
          >
            <span className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-navy-card text-txt-faint">
              <Info size={16} />
            </span>
            <p className="text-[12.5px] leading-[1.5] text-txt-muted">
              No suggestions yet. Click ‘AI Once-Over’ to scan this letter.
            </p>
            {onRunSweep && (
              <button
                type="button"
                onClick={onRunSweep}
                disabled={sweeping}
                data-testid="ccl-rail-run-sweep"
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-teal px-3 py-1.5 text-[12px] font-medium text-white transition-colors duration-150 hover:bg-teal-hover disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/70"
              >
                {sweeping ? (
                  <><Loader2 size={13} className="animate-spin" /> Scanning…</>
                ) : (
                  <><Sparkles size={13} /> AI Once-Over</>
                )}
              </button>
            )}
          </div>
        ) : (
          <ul className="flex flex-col gap-2.5" data-testid="ccl-rail-list">
            {visible.map((comment) => (
              <CommentCard
                key={comment.id}
                comment={comment}
                authorLabel={label}
                active={
                  comment.anchorId != null &&
                  comment.anchorId === activeAnchorId
                }
                cardRef={(el) => {
                  cardRefs.current[comment.id] = el;
                }}
                onAccept={onAccept}
                onReject={onReject}
                onUndo={onUndo}
                onReply={onReply}
                onDraft={onDraft}
                onEditDraft={onEditDraft}
                onDiscard={onDiscard}
                onFocusAnchor={onFocusAnchor}
              />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
